import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { giftsApi, peopleApi, sharingApi } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/shared/Toast";
import ModuleHeader from "../components/shared/ModuleHeader";
import SavingNotice from "../components/shared/SavingNotice";
import CenteredModal from "../components/shared/CenteredModal";
import ConfirmModal from "../components/shared/ConfirmModal";
import useMinLoad from "../hooks/useMinLoad";
import BackButtonTrap from "../components/shared/BackButtonTrap";
import useOnEscape from "../hooks/useOnEscape";
import useFormPresentation from "../hooks/useFormPresentation";

const IMG_PEOPLE = "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1400&q=75&auto=format&fit=crop";
const HEADER_PHOTO_LIMIT = 8;
const HEADER_PHOTO_MODES = {
  AUTO: "auto",
  CHOOSE: "choose",
  DEFAULT: "default",
};

function isUploadedProfilePhoto(value) {
  return typeof value === "string" && (
    value.startsWith("data:image/") ||
    value.startsWith("https://") ||
    value.startsWith("http://")
  );
}

const RELATIONSHIPS = [
  "Partner", "Spouse", "Mom", "Dad", "Sister", "Brother",
  "Daughter", "Son", "Baby", "Toddler", "Child", "Teen",
  "Grandmother", "Grandfather", "Friend", "Best friend",
  "Colleague", "Boss", "Other"
];

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const AVATAR_COLORS = [
  ["#FDEEE9","#E7614A"],["#EAF3EE","#4A7C59"],
  ["#FDF6E3","#C9A84C"],["#EEF2FF","#6366F1"],
  ["#FDF2F8","#C026D3"],["#E0F2FE","#0369A1"],
];

const INTEREST_OPTIONS = [
  "Books", "Beauty", "Coffee", "Cooking", "Fashion", "Fitness",
  "Gaming", "Home decor", "Music", "Pets", "Tech", "Travel",
  "Animals", "Building toys", "Cars & trucks", "Dinosaurs",
  "Dress-up", "Picture books", "Pretend play", "Sensory play"
];

const HOBBY_OPTIONS = [
  "Baking", "Camping", "Gardening", "Gym", "Hiking", "Painting",
  "Photography", "Reading", "Running", "Traveling", "Yoga", "DIY",
  "Art time", "Bath toys", "Blocks", "Coloring",
  "Outdoor play", "Puzzles", "Story time", "Stuffed animals"
];

const DISLIKE_OPTIONS = [
  "Alcohol", "Clutter", "Crowds", "Jewelry", "Novelty gifts", "Perfume",
  "Strong scents", "Tech gadgets", "Very expensive gifts", "Wasteful items",
  "Loud toys", "Messy kits", "Small parts", "Too advanced", "Screen-heavy gifts"
];

function getAvatarColor(name) {
  return AVATAR_COLORS[(name||"?").charCodeAt(0) % AVATAR_COLORS.length];
}

function getInitials(name) {
  return (name || "?").split(" ").map(word => word[0]).join("").slice(0, 2).toUpperCase();
}

function sortUpcomingOccasions(person) {
  return [...(person?.occasions || [])]
    .filter(occasion => occasion.days_until !== null && occasion.days_until !== undefined)
    .sort((a, b) => (a.days_until ?? 9999) - (b.days_until ?? 9999));
}

function dayLabel(days) {
  if (days === 0) return "Today";
  if (days === 1) return "1d";
  return `${days}d`;
}

function urgencyClass(days) {
  if (days === null || days === undefined) return "is-calm";
  if (days <= 14) return "is-urgent";
  if (days <= 30) return "is-soon";
  return "is-calm";
}

function occasionLabel(type) {
  return String(type || "occasion").replace(/_/g, " ");
}

function occasionEmoji(type) {
  const key = String(type || "").toLowerCase();
  if (key.includes("birthday")) return "🎂";
  if (key.includes("wedding") || key.includes("anniversary")) return "💍";
  if (key.includes("graduation")) return "🎓";
  if (key.includes("christmas")) return "🎄";
  if (key.includes("shower")) return "🍼";
  if (key.includes("party")) return "🎉";
  return "🎁";
}

function formatBudgetRange(person) {
  const min = Number(person?.budget_min || 0);
  const max = Number(person?.budget_max || 0);
  if (min > 0 && max > 0) return `$${min}-${max}`;
  if (min > 0) return `$${min}+`;
  if (max > 0) return `Up to $${max}`;
  return "Not set";
}

function useSheetHandleDrag(onClose, threshold = 46) {
  const sheetRef = useRef(null);
  const dragRef = useRef({ active: false, startY: 0, currentY: 0 });

  const startDrag = useCallback((clientY) => {
    dragRef.current = { active: true, startY: clientY, currentY: 0 };
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  }, []);

  const moveDrag = useCallback((clientY, event) => {
    if (!dragRef.current.active || !sheetRef.current) return;
    const delta = Math.max(0, clientY - dragRef.current.startY);
    dragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    sheetRef.current.style.transform = `translateY(${delta}px)`;
  }, []);

  const finishDrag = useCallback(() => {
    if (!dragRef.current.active || !sheetRef.current) return;
    const delta = dragRef.current.currentY;
    dragRef.current = { active: false, startY: 0, currentY: 0 };
    if (delta > threshold) {
      sheetRef.current.style.transition = "transform .2s cubic-bezier(0.4, 0, 1, 1)";
      sheetRef.current.style.transform = "translateY(100%)";
      window.setTimeout(onClose, 205);
      return;
    }
    sheetRef.current.style.transition = "transform .18s cubic-bezier(0.2, 0, 0, 1)";
    sheetRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!sheetRef.current || dragRef.current.active) return;
      sheetRef.current.style.transform = "";
      sheetRef.current.style.transition = "";
    }, 190);
  }, [onClose, threshold]);

  const onPointerDown = useCallback((event) => {
    startDrag(event.clientY);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [startDrag]);

  const onPointerMove = useCallback((event) => {
    moveDrag(event.clientY, event);
  }, [moveDrag]);

  const onPointerUp = useCallback((event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    finishDrag();
  }, [finishDrag]);

  const onTouchStart = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    startDrag(touch.clientY);
  }, [startDrag]);

  const onTouchMove = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    moveDrag(touch.clientY, event);
  }, [moveDrag]);

  return {
    sheetRef,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onTouchStart,
      onTouchMove,
      onTouchEnd: finishDrag,
      onTouchCancel: finishDrag,
      role: "button",
      tabIndex: 0,
      "aria-label": "Drag down to close",
      style: { cursor: "grab", touchAction: "none" },
    },
  };
}

function GiftSheetTopRow({ onClose, handleProps = {}, closeLabel = "Close" }) {
  return (
    <div className="gf-sheet-top-row">
      <button
        type="button"
        className="gf-sheet-handle"
        {...handleProps}
        onClick={(event) => {
          handleProps.onClick?.(event);
          if (!event.defaultPrevented) onClose?.();
        }}
      />
      <button type="button" className="gf-sheet-top-close" onClick={onClose} aria-label={closeLabel}>×</button>
    </div>
  );
}

function GiftSheetHeader({ title, icon = "⌂" }) {
  return (
    <div className="gf-sheet-logo-row">
      <div className="gf-sheet-brand">
        <div className="gf-sheet-logo-ico" aria-hidden="true">{icon}</div>
        <span className="gf-sheet-title">{title}</span>
      </div>
    </div>
  );
}

function GiftsIntelligenceHeader({
  nextOccasion,
  pendingGiftCount,
  pendingGiftAction,
  annualSpend,
  peopleCount,
  onOpenGiftAction,
}) {
  const [expanded, setExpanded] = useState(() => {
    try {
      return window.localStorage.getItem("gifts_header_expanded") === "true";
    } catch {
      return false;
    }
  });
  const hasPending = pendingGiftCount > 0;
  const allReady = !hasPending;
  const headerText = hasPending
    ? `${pendingGiftCount} gift${pendingGiftCount === 1 ? "" : "s"} pending · People & Gifts`
    : "All gifts ready · People & Gifts";
  const toggleExpanded = () => {
    setExpanded((current) => {
      const next = !current;
      try {
        window.localStorage.setItem("gifts_header_expanded", String(next));
      } catch {
        // Keep the drawer usable even if browser storage is unavailable.
      }
      return next;
    });
  };

  return (
    <section className={`gf-intel-card${expanded ? " is-expanded" : " is-collapsed"}${allReady ? " is-ready" : ""}`}>
      <button type="button" className="gf-intel-head" onClick={toggleExpanded} aria-expanded={expanded}>
        <span className="gf-intel-icon">🎁</span>
        <strong>{headerText}</strong>
        <i className="gf-intel-chevron">›</i>
      </button>
      {expanded ? (
        <div className="gf-intel-body">
          <div className="gf-intel-stat-grid">
            <div className="is-teal">
              <strong>${annualSpend.toFixed(0)}</strong>
              <span>Spent this year</span>
            </div>
            <div>
              <strong>{peopleCount}</strong>
              <span>People tracked</span>
            </div>
            <div className={hasPending ? "is-danger" : "is-good"}>
              <strong>{pendingGiftCount}</strong>
              <span>Gifts pending</span>
            </div>
            <div className={(nextOccasion?.occasion.days_until ?? 9999) < 30 ? "is-warn" : ""}>
              <strong>{nextOccasion ? dayLabel(nextOccasion.occasion.days_until) : "None"}</strong>
              <span>{nextOccasion ? "Next occasion" : "No upcoming"}</span>
            </div>
          </div>
          {hasPending && pendingGiftAction ? (
            <button type="button" className="gf-intel-action" onClick={() => onOpenGiftAction(pendingGiftAction)}>
              <span>🎁</span>
              <strong>{pendingGiftCount} gift{pendingGiftCount === 1 ? "" : "s"} still to buy · {pendingGiftAction.person_name} · {occasionLabel(pendingGiftAction.occasion_type)} in {pendingGiftAction.days_until} days</strong>
              <em>›</em>
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function NextOccasionDrawer({ item, onGiftIdeas, onCardMessage, cardLoading }) {
  const [expanded, setExpanded] = useState(() => {
    try {
      return window.localStorage.getItem("next_occasion_expanded") === "true";
    } catch {
      return false;
    }
  });
  if (!item || (item.occasion.days_until ?? 9999) > 365) return null;

  const { person, occasion } = item;
  const pendingCount = Number(occasion.gift_pending_count || 0);
  const purchasedCount = Number(occasion.gift_purchased_count || 0);
  const giftReady = purchasedCount > 0;
  const days = Number(occasion.days_until || 0);
  const toggleExpanded = () => {
    setExpanded((current) => {
      const next = !current;
      try {
        window.localStorage.setItem("next_occasion_expanded", String(next));
      } catch {
        // Keep the drawer usable even if browser storage is unavailable.
      }
      return next;
    });
  };

  return (
    <section className={`gf-next-occasion-card${expanded ? " is-expanded" : " is-collapsed"}`}>
      <button type="button" className="gf-next-occasion-head" onClick={toggleExpanded} aria-expanded={expanded}>
        <span className="gf-next-occasion-icon">{occasionEmoji(occasion.occasion_type)}</span>
        <strong>Next: {person.name}'s {occasionLabel(occasion.occasion_type)} in {days} days</strong>
        <span>{dayLabel(days)}</span>
        <i>›</i>
      </button>
      {expanded ? (
        <div className="gf-next-occasion-body">
          <div className="gf-next-occasion-summary">
            {person.name} · {dayLabel(days)} · {occasionLabel(occasion.occasion_type)}
          </div>
          <div className="gf-next-occasion-stats">
            <div>
              <strong>{formatBudgetRange(person)}</strong>
              <span>Budget range</span>
            </div>
            <div className={days < 30 ? "is-warn" : "is-good"}>
              <strong>{days}</strong>
              <span>Days until</span>
            </div>
            <div className={pendingCount > 0 ? "is-danger" : "is-good"}>
              <strong>{pendingCount > 0 ? pendingCount : "Ready"}</strong>
              <span>Gifts pending</span>
            </div>
            <div>
              <strong>{person.last_gift_name || "None yet"}</strong>
              <span>Last gift given</span>
            </div>
          </div>
          <div className={`gf-next-gift-status${giftReady ? " is-ready" : " is-needed"}`}>
            {giftReady ? "✓ Gift ready" : "Gift needed"}
          </div>
          <div className="gf-next-occasion-actions">
            <button type="button" className="is-primary" onClick={() => onGiftIdeas(person, occasion)}>
              ✦ Get gift ideas ↗
            </button>
            <button type="button" onClick={() => onCardMessage(person, occasion)} disabled={cardLoading}>
              {cardLoading ? "Writing..." : "💌 Write card message ↗"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

const PRESET_AVATAR_OPTIONS = [
  { label: "Star", emoji: "⭐" },
  { label: "Sparkles", emoji: "✨" },
  { label: "Heart", emoji: "💖" },
  { label: "Gift", emoji: "🎁" },
  { label: "Balloon", emoji: "🎈" },
  { label: "Crown", emoji: "👑" },
  { label: "Flower", emoji: "🌸" },
  { label: "Teddy", emoji: "🧸" },
];

function PersonActionSheet({
  person,
  canShare,
  onClose,
  onOpenProfile,
  onEdit,
  onAddOccasion,
  onViewOccasions,
  onShare,
  onRemove,
}) {
  const presentation = useFormPresentation();
  if (!person) return null;
  const nextOcc = sortUpcomingOccasions(person)[0];
  const subtitle = [person.relationship, nextOcc ? `${occasionLabel(nextOcc.occasion_type)} in ${nextOcc.days_until} days` : null].filter(Boolean).join(" · ");
  const actions = (
      <div className="hm-gifts-sheet-actions">
        <button type="button" onClick={onOpenProfile}>
          <span>👤</span>
          <span><strong>Open profile</strong><small>Full profile, occasions &amp; gift history</small></span>
          <em>›</em>
        </button>
        <button type="button" onClick={onViewOccasions}>
          <span>🗓️</span>
          <span><strong>View occasions</strong><small>Jump straight to planned occasions</small></span>
          <em>›</em>
        </button>
        <button type="button" onClick={onAddOccasion}>
          <span>📅</span>
          <span><strong>Add occasion</strong><small>Birthday, Christmas, anniversary and more</small></span>
          <em>›</em>
        </button>
        <button type="button" onClick={onEdit}>
          <span>✏️</span>
          <span><strong>Edit person</strong><small>Update interests, budget, relationship</small></span>
          <em>›</em>
        </button>
        {canShare && (
          <button type="button" onClick={onShare}>
            <span>👥</span>
            <span><strong>Share with Family</strong><small>Family plan members can see this profile</small></span>
            <em>›</em>
          </button>
        )}
        <button type="button" className="is-danger" onClick={onRemove}>
          <span>🗑️</span>
          <span><strong>Remove person</strong></span>
        </button>
      </div>
  );

  if (presentation === "modal") {
    return (
      <CenteredModal title={person.name} onClose={onClose}>
        {subtitle && <div className="gf-action-subtitle">{subtitle}</div>}
        {actions}
      </CenteredModal>
    );
  }

  return (
    <PeopleSheet title={person.name} icon="👤" onClose={onClose} className="gf-action-menu-sheet">
      {subtitle && <div className="gf-action-subtitle">{subtitle}</div>}
      {actions}
    </PeopleSheet>
  );
}

function AvatarUpload({ name, photoUrl, onChange }) {
  const fileRef = useRef();
  const [preview, setPreview] = useState(photoUrl || null);
  const [uploadError, setUploadError] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [bg, fg] = getAvatarColor(name || "?");
  const initials = getInitials(name);
  const selectedPresetEmoji = selectedPreset ? (PRESET_AVATAR_OPTIONS.find(option => option.label === selectedPreset)?.emoji || "") : "";

  useEffect(() => {
    setPreview(photoUrl || null);
    const matchingPreset = PRESET_AVATAR_OPTIONS.find(option => option.emoji === photoUrl);
    setSelectedPreset(matchingPreset?.label || "");
  }, [photoUrl]);

  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const maxDimension = 512;
        const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("Could not process this image."));
          return;
        }
        context.drawImage(image, 0, 0, width, height);
        const compressed = canvas.toDataURL("image/jpeg", 0.82);
        resolve(compressed);
      };
      image.onerror = () => reject(new Error("Could not read this image."));
      image.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Could not load this file."));
    reader.readAsDataURL(file);
  });

  const handleFile = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError(null);
    try {
      const compressed = await compressImage(file);
      setPreview(compressed);
      onChange(compressed);
    } catch (error) {
      setUploadError(error.message || "Could not prepare that image.");
    } finally {
      e.target.value = "";
    }
  };

  const handlePresetSelect = (label) => {
    const avatar = PRESET_AVATAR_OPTIONS.find(option => option.label === label)?.emoji || "";
    setUploadError(null);
    setSelectedPreset(label);
    setPreview(avatar);
    onChange(avatar);
  };

  const handleRemovePhoto = () => {
    setUploadError(null);
    setSelectedPreset("");
    setPreview(null);
    onChange("");
  };

  return (
    <div className="person-avatar-upload">
      <div
        className="person-avatar-upload-preview"
        style={{
          width:80, height:80, borderRadius:"50%",
          background: preview ? "transparent" : bg,
          color: fg, display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:28, fontFamily:"var(--font-display)", fontWeight:300,
          overflow:"hidden",
        }}
      >
        {preview && !selectedPresetEmoji
          ? <img src={preview} alt="avatar" style={{width:"100%",height:"100%",objectFit:"cover"}} />
          : selectedPresetEmoji || initials}
      </div>
      <div className="person-avatar-upload-controls">
        <div className="person-avatar-upload-buttons">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>
            Upload photo
          </button>
          {preview && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={handleRemovePhoto}>
              Remove
            </button>
          )}
        </div>
        <div className="person-avatar-upload-select">
          <label className="form-label" style={{ fontSize: 12, marginBottom: 6 }}>Avatar style</label>
          <select
            className="form-select"
            value={selectedPreset}
            onChange={(e) => {
              const { value } = e.target;
              if (!value) {
                setSelectedPreset("");
                return;
              }
              handlePresetSelect(value);
            }}
          >
            <option value="">Choose an emoji avatar</option>
            {PRESET_AVATAR_OPTIONS.map((option) => (
              <option key={option.label} value={option.label}>{option.emoji} {option.label}</option>
            ))}
          </select>
        </div>
        <span className="person-avatar-upload-hint">
          This photo appears on the person card and profile.
        </span>
        {uploadError && (
          <span className="person-avatar-upload-error">{uploadError}</span>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleFile} />
    </div>
  );
}

function normalizeTags(values = []) {
  const seen = new Set();
  return values
    .map(value => String(value).trim())
    .filter(value => {
      const lowered = value.toLowerCase();
      if (!value || seen.has(lowered)) return false;
      seen.add(lowered);
      return true;
    });
}

function TagSelector({ label, options, values, onChange, placeholder }) {
  const [custom, setCustom] = useState("");

  const toggle = (option) => {
    const exists = values.includes(option);
    onChange(exists ? values.filter(v => v !== option) : [...values, option]);
  };

  const addCustom = () => {
    const value = custom.trim();
    if (!value) return;
    onChange(normalizeTags([...values, value]));
    setCustom("");
  };

  return (
    <div className="form-group tag-selector-group">
      <div className="flex-between gap-8" style={{ alignItems: "baseline" }}>
        <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>
        <span className="text-sm" style={{ color: "var(--ink-hint)" }}>
          Tap to select
        </span>
      </div>
      <div className="tag-selector-shell">
        <div className="tag-selector-copy">
          Pick from common options first, then add anything custom below.
        </div>
        <div className="tag-selector-options">
        {options.map(option => {
          const selected = values.includes(option);
          return (
            <button
              key={option}
              type="button"
              className={`tag-option${selected ? " is-selected" : ""}`}
              onClick={() => toggle(option)}
              aria-pressed={selected}
              style={{
                background: selected ? "var(--rose-light)" : "var(--warm-white)",
                color: selected ? "var(--rose-dark)" : "var(--ink-soft)",
                border: `1px solid ${selected ? "var(--rose)" : "var(--border-strong)"}`,
              }}
            >
              <span>{option}</span>
              {selected && <span style={{ fontSize: 12 }}>✓</span>}
            </button>
          );
        })}
        </div>
      </div>
      <div className="tag-input-row" style={{ display: "flex", gap: 8 }}>
        <input
          className="form-input"
          value={custom}
          onChange={e => setCustom(e.target.value)}
          placeholder={placeholder}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
        />
        <button type="button" className="btn btn-secondary" onClick={addCustom}>Add</button>
      </div>
      {values.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          {values.map(value => (
            <span key={value} className="badge selected-tag-badge">
              {value}
              <button
                type="button"
                className="tag-remove-btn"
                onClick={() => onChange(values.filter(item => item !== value))}
                aria-label={`Remove ${value}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Mobile-only tag pill for the 3-step Add Person sheet ─────────────── */
function GfTagSection({ label, tags, selected, onToggle, onCustomAdd, customPlaceholder }) {
  const [customValue, setCustomValue] = useState("");
  const selectedTags = normalizeTags(selected);
  const remainingTags = tags.filter(value => !selectedTags.includes(value));
  const addCustom = () => {
    const value = customValue.trim();
    if (!value || !onCustomAdd) return;
    onCustomAdd(value);
    setCustomValue("");
  };

  return (
    <div className="gf-field">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <label className="gf-label">{label}</label>
        {selectedTags.length > 0 && (
          <span style={{ fontSize: 10, color: "var(--hm-mint)", fontWeight: 700, background: "color-mix(in srgb, var(--hm-mint) 12%, transparent)", padding: "2px 8px", borderRadius: 20 }}>
            {selectedTags.length} selected
          </span>
        )}
      </div>
      {selectedTags.length > 0 && (
        <div className="gf-tag-block gf-tag-block--selected">
          <span>Selected</span>
          <div className="gf-tags">
            {selectedTags.map(t => (
              <button
                key={t}
                type="button"
                className="gf-tag gf-tag--on"
                onClick={() => onToggle(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="gf-tag-block">
        {selectedTags.length > 0 && <span>More options</span>}
        <div className="gf-tags">
          {remainingTags.map(t => (
            <button
              key={t}
              type="button"
              className="gf-tag"
              onClick={() => onToggle(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      {onCustomAdd && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 76px", gap: 8, marginTop: 8, width: "100%" }}>
          <input
            className="hm-input"
            style={{ fontSize: 12, width: "100%" }}
            placeholder={customPlaceholder}
            value={customValue}
            onChange={e => setCustomValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCustom();
              }
            }}
          />
          <button type="button" className="hm-btn hm-btn--secondary" style={{ width: "100%", padding: "0 12px" }} onClick={addCustom}>
            Add
          </button>
        </div>
      )}
    </div>
  );
}

/* ── 3-step Add/Edit Person bottom sheet (mobile only) ─────────────────────── */
function AddPersonSheet({ initial = {}, title = "Add a person", submitLabel = "✓ Save person", onSave, onClose, loading, error }) {
  const [step, setStep] = useState(1);
  const fileRef = useRef();
  const { sheetRef, handleProps } = useSheetHandleDrag(onClose);
  const parseBirthDate = (birthDate) => {
    if (!birthDate) return { month: "", day: "", year: "" };
    const parts = String(birthDate).split("-");
    return {
      month: String(parseInt(parts[1] || 0, 10) || ""),
      day: String(parseInt(parts[2] || 0, 10) || ""),
      year: parts[0] === "2000" ? "" : (parts[0] || ""),
    };
  };
  const parsedBirthDate = parseBirthDate(initial.birth_date);

  const [form, setForm] = useState({
    name: initial.name || "",
    relationship: initial.relationship || "Friend",
    phone: initial.phone || "",
    email: initial.email || "",
    photo_url: initial.photo_url || "",
    birth_month: parsedBirthDate.month,
    birth_day: parsedBirthDate.day,
    birth_year: parsedBirthDate.year,
    interest_tags: initial.interest_tags || [],
    hobby_tags: initial.hobby_tags || [],
    dislike_tags: initial.dislike_tags || [],
    interests: initial.interests || "",
    dislikes: initial.dislikes || "",
    notes: initial.notes || "",
    favorite_foods: initial.favorite_foods || "",
    favorite_stores: initial.favorite_stores || "",
    gift_preferences: initial.gift_preferences || "",
    sizes: initial.sizes || "",
    allergies: initial.allergies || "",
    favorite_restaurants: initial.favorite_restaurants || "",
    traditions: initial.traditions || "",
    budget_min: initial.budget_min || 25,
    budget_max: initial.budget_max || 100,
  });

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function canAdvance(s) {
    if (s === 1) return form.name.trim().length > 0 && Boolean(form.relationship);
    return true;
  }

  function buildPayload() {
    const payload = {
      name: form.name,
      relationship: form.relationship,
      phone: form.phone,
      email: form.email,
      photo_url: form.photo_url || "",
      interests: form.interests,
      interest_tags: normalizeTags(form.interest_tags),
      hobby_tags: normalizeTags(form.hobby_tags),
      dislikes: form.dislikes,
      dislike_tags: normalizeTags(form.dislike_tags),
      notes: form.notes,
      favorite_foods: form.favorite_foods,
      favorite_stores: form.favorite_stores,
      gift_preferences: form.gift_preferences,
      sizes: form.sizes,
      allergies: form.allergies,
      favorite_restaurants: form.favorite_restaurants,
      traditions: form.traditions,
      budget_min: parseInt(form.budget_min) || 25,
      budget_max: parseInt(form.budget_max) || 100,
    };
    if (form.birth_month && form.birth_day) {
      const m = String(form.birth_month).padStart(2, "0");
      const d = String(form.birth_day).padStart(2, "0");
      payload.birth_date = `${form.birth_year || "2000"}-${m}-${d}`;
      if (!form.birth_year) payload.age = null;
    }
    return payload;
  }

  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const maxDimension = 512;
        const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Could not process image.")); return; }
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      image.onerror = () => reject(new Error("Could not read image."));
      image.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Could not load file."));
    reader.readAsDataURL(file);
  });

  const handleFile = async e => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setField("photo_url", compressed);
    } finally {
      e.target.value = "";
    }
  };

  const [bg, fg] = getAvatarColor(form.name || "?");
  const initials = getInitials(form.name || "?");

  const toggleTag = (field, val) => {
    const cur = form[field] || [];
    setField(field, cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val]);
  };
  const addCustomTag = (field, value) => {
    const cur = form[field] || [];
    setField(field, normalizeTags([...cur, value]));
  };

  return (
    <div
      className="fd-clear-overlay"
      style={{ zIndex: 5500 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="gf-add-person-sheet"
        ref={sheetRef}
      >
        <GiftSheetTopRow onClose={onClose} handleProps={handleProps} closeLabel="Close person form" />
        <GiftSheetHeader title={title} />

        {/* Step indicator */}
        <div className="gf-steps-row">
          {[1, 2, 3].map(n => (
            <div
              key={n}
              className={`gf-step-dot${n < step ? " gf-step-dot--done" : ""}${n === step ? " gf-step-dot--active" : ""}`}
            />
          ))}
          <span className="gf-step-txt">STEP {step} OF 3</span>
        </div>

        {/* Step body */}
        <div className="gf-sheet-body">

          {/* ── Step 1: Basic info ───────────────────────── */}
          {step === 1 && (
            <>
              <div className="gf-avatar-row">
                <div className="gf-avatar-circle" style={{ background: form.photo_url ? "transparent" : bg, color: fg }}>
                  {form.photo_url
                    ? <img src={form.photo_url} alt="" className="gf-avatar-img" />
                    : <span style={{ fontSize: 20, fontWeight: 600 }}>{initials}</span>
                  }
                </div>
                <button type="button" className="gf-upload-btn" onClick={() => fileRef.current?.click()}>
                  ↑ Upload photo
                </button>
                {form.photo_url && (
                  <button
                    type="button"
                    className="gf-upload-btn"
                    style={{ color: "var(--hm-muted)", borderColor: "var(--hm-line)" }}
                    onClick={() => setField("photo_url", "")}
                  >
                    Remove
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
              </div>

              <div className="gf-field">
                <label className="gf-label">Name *</label>
                <input
                  className="hm-input"
                  value={form.name}
                  onChange={e => setField("name", e.target.value)}
                  placeholder="Sally"
                  autoFocus
                />
              </div>

              <div className="gf-field">
                <label className="gf-label">Relationship *</label>
                <select className="hm-select" value={form.relationship} onChange={e => setField("relationship", e.target.value)}>
                  <option value="">Select...</option>
                  {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div className="gf-2col">
                <div className="gf-field"><label className="gf-label">Phone <span className="gf-opt">(optional)</span></label><input className="hm-input" type="tel" value={form.phone} onChange={e => setField("phone", e.target.value)} placeholder="(555) 555-0123" /></div>
                <div className="gf-field"><label className="gf-label">Email <span className="gf-opt">(optional)</span></label><input className="hm-input" type="email" value={form.email} onChange={e => setField("email", e.target.value)} placeholder="name@example.com" /></div>
              </div>

              <div className="gf-field">
                <label className="gf-label">Birthday <span className="gf-opt">(optional)</span></label>
                <div className="gf-2col">
                  <select className="hm-select" value={form.birth_month} onChange={e => setField("birth_month", e.target.value)}>
                    <option value="">Month</option>
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <select className="hm-select" value={form.birth_day} onChange={e => setField("birth_day", e.target.value)}>
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: Preferences ──────────────────────── */}
          {step === 2 && (
            <>
              <GfTagSection
                label="Interests"
                tags={INTEREST_OPTIONS}
                selected={form.interest_tags}
                onToggle={v => toggleTag("interest_tags", v)}
                onCustomAdd={v => addCustomTag("interest_tags", v)}
                customPlaceholder="Add custom interests..."
              />
              <GfTagSection
                label="Hobbies"
                tags={HOBBY_OPTIONS}
                selected={form.hobby_tags}
                onToggle={v => toggleTag("hobby_tags", v)}
                onCustomAdd={v => addCustomTag("hobby_tags", v)}
                customPlaceholder="Add custom hobbies..."
              />
              <GfTagSection
                label="Avoid / dislikes"
                tags={DISLIKE_OPTIONS}
                selected={form.dislike_tags}
                onToggle={v => toggleTag("dislike_tags", v)}
                onCustomAdd={v => addCustomTag("dislike_tags", v)}
                customPlaceholder="Add custom avoids..."
              />
            </>
          )}

          {/* ── Step 3: Details ──────────────────────────── */}
          {step === 3 && (
            <>
              <div className="gf-memory-card">
                <div className="gf-memory-title">♥ Relationship memory</div>
                <div className="gf-memory-sub">Used by HouseMind for smarter gift ideas</div>
                {[
                  { key: "favorite_foods",       label: "Favourite foods",       ph: "Sushi, jollof rice..." },
                  { key: "favorite_stores",       label: "Favourite stores",      ph: "Target, Sephora..." },
                  { key: "gift_preferences",      label: "Gift preferences",      ph: "Experiences, practical..." },
                  { key: "sizes",                 label: "Sizes",                 ph: "Shirt M, shoe 8..." },
                  { key: "allergies",             label: "Allergies / dietary",   ph: "Peanuts, gluten free..." },
                  { key: "favorite_restaurants",  label: "Favourite restaurants", ph: "Date night spots..." },
                  { key: "traditions",            label: "Traditions",            ph: "Flowers on Mother's Day..." },
                ].map(({ key, label, ph }) => (
                  <div key={key} className="gf-field" style={{ gap: 3 }}>
                    <label className="gf-label" style={{ fontSize: 11 }}>{label}</label>
                    <input
                      className="hm-input"
                      style={{ fontSize: 12, padding: "7px 10px" }}
                      placeholder={ph}
                      value={form[key] || ""}
                      onChange={e => setField(key, e.target.value)}
                    />
                  </div>
                ))}
              </div>

              <div className="gf-field">
                <label className="gf-label">Gift budget</label>
                <div className="gf-2col">
                  <div>
                    <label className="gf-label" style={{ fontSize: 11, marginBottom: 4 }}>Min ($)</label>
                    <input
                      className="hm-input"
                      type="number"
                      inputMode="numeric"
                      value={form.budget_min || ""}
                      placeholder="25"
                      onChange={e => setField("budget_min", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="gf-label" style={{ fontSize: 11, marginBottom: 4 }}>Max ($)</label>
                    <input
                      className="hm-input"
                      type="number"
                      inputMode="numeric"
                      value={form.budget_max || ""}
                      placeholder="100"
                      onChange={e => setField("budget_max", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="gf-field">
                <label className="gf-label">Notes <span className="gf-opt">(optional)</span></label>
                <input
                  className="hm-input"
                  placeholder="Lives in a small apartment..."
                  value={form.notes || ""}
                  onChange={e => setField("notes", e.target.value)}
                />
              </div>

              {error && (
                <div className="notice-banner is-error" style={{ margin: "4px 0" }}>
                  <p>{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="fd-clear-sheet__foot gf-sheet-foot">
          {step === 1 ? (
            <button type="button" className="fd-clear-cancel" onClick={onClose}>Cancel</button>
          ) : (
            <button type="button" className="fd-clear-cancel" onClick={() => setStep(s => s - 1)}>← Back</button>
          )}
          {step < 3 ? (
            <button
              type="button"
              className="hm-btn hm-btn--primary fd-clear-confirm"
              disabled={!canAdvance(step)}
              onClick={() => setStep(s => s + 1)}
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              className="hm-btn hm-btn--primary fd-clear-confirm"
              disabled={loading}
              onClick={() => onSave(buildPayload())}
            >
              {loading ? "Saving..." : submitLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PersonForm({ initial={}, onSave, onCancel, loading, onPhotoAutoSave, photoSaving }) {
  // Parse existing birth_date into month/day/year
  const parseBirthDate = (bd) => {
    if (!bd) return { month:"", day:"", year:"" };
    const parts = bd.split("-");
    const parsedYear = parts[0] === "2000" ? "" : (parts[0] || "");
    return { year: parsedYear, month: String(parseInt(parts[1]||0)), day: String(parseInt(parts[2]||0)) };
  };

  const parsed = parseBirthDate(initial.birth_date);

  const [form, setForm] = useState({
    name: initial.name||"",
    relationship: initial.relationship||"Friend",
    phone: initial.phone||"",
    email: initial.email||"",
    interests: initial.interests||"",
    interest_tags: initial.interest_tags || [],
    hobby_tags: initial.hobby_tags || [],
    dislikes: initial.dislikes||"",
    dislike_tags: initial.dislike_tags || [],
    notes: initial.notes||"",
    favorite_foods: initial.favorite_foods||"",
    favorite_stores: initial.favorite_stores||"",
    gift_preferences: initial.gift_preferences||"",
    sizes: initial.sizes||"",
    allergies: initial.allergies||"",
    favorite_restaurants: initial.favorite_restaurants||"",
    traditions: initial.traditions||"",
    budget_min: initial.budget_min||25,
    budget_max: initial.budget_max||100,
    photo_url: initial.photo_url||"",
    birth_month: parsed.month,
    birth_day: parsed.day,
    birth_year: parsed.year,
  });

  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  // Generate days 1-31
  const days = Array.from({length:31},(_,i)=>i+1);

  const buildPayload = (nextForm) => {
    const payload = {
      name: nextForm.name,
      relationship: nextForm.relationship,
      phone: nextForm.phone,
      email: nextForm.email,
      interests: nextForm.interests,
      interest_tags: normalizeTags(nextForm.interest_tags),
      hobby_tags: normalizeTags(nextForm.hobby_tags),
      dislikes: nextForm.dislikes,
      dislike_tags: normalizeTags(nextForm.dislike_tags),
      notes: nextForm.notes,
      favorite_foods: nextForm.favorite_foods,
      favorite_stores: nextForm.favorite_stores,
      gift_preferences: nextForm.gift_preferences,
      sizes: nextForm.sizes,
      allergies: nextForm.allergies,
      favorite_restaurants: nextForm.favorite_restaurants,
      traditions: nextForm.traditions,
      budget_min: parseInt(nextForm.budget_min)||25,
      budget_max: parseInt(nextForm.budget_max)||100,
      photo_url: nextForm.photo_url || "",
    };

    if (nextForm.birth_month && nextForm.birth_day) {
      const year = nextForm.birth_year || "2000";
      const m = String(nextForm.birth_month).padStart(2,"0");
      const d = String(nextForm.birth_day).padStart(2,"0");
      payload.birth_date = `${year}-${m}-${d}`;
      if (!nextForm.birth_year) payload.age = null;
    }

    return payload;
  };

  const handleSubmit = e => {
    e.preventDefault();
    onSave(buildPayload(form));
  };

  const handlePhotoChange = async (url) => {
    const nextForm = { ...form, photo_url: url };
    setForm(nextForm);
    if (!initial.id || !onPhotoAutoSave) return;
    await onPhotoAutoSave(buildPayload(nextForm), { isRemoving: !url });
  };

  return (
    <form onSubmit={handleSubmit}>
      <AvatarUpload
        name={form.name}
        photoUrl={form.photo_url}
        onChange={handlePhotoChange}
      />

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Name *</label>
          <input className="form-input" value={form.name} onChange={set("name")} placeholder="Sally" required />
        </div>
        <div className="form-group">
          <label className="form-label">Relationship *</label>
          <select className="form-select" value={form.relationship} onChange={set("relationship")}>
            {RELATIONSHIPS.map(r=><option key={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <div className="grid-2">
        <div className="form-group"><label className="form-label">Phone</label><input className="form-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 555-0123" /></div>
        <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={set("email")} placeholder="name@example.com" /></div>
      </div>

      {/* Birthday: month + day required, year optional */}
      <div className="form-group">
        <label className="form-label">Birthday</label>
        <div className="person-birthday-row" style={{display:"flex", gap:8}}>
          <select className="form-select" value={form.birth_month} onChange={set("birth_month")} style={{flex:2}}>
            <option value="">Month</option>
            {MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}
          </select>
          <select className="form-select" value={form.birth_day} onChange={set("birth_day")} style={{flex:1}}>
            <option value="">Day</option>
            {days.map(d=><option key={d} value={d}>{d}</option>)}
          </select>
          <input
            className="form-input" type="number" placeholder="Year (optional)"
            value={form.birth_year} onChange={set("birth_year")}
            min="1900" max={new Date().getFullYear()} style={{flex:2}}
          />
        </div>
        <span className="text-sm" style={{ color:"var(--ink-hint)", marginTop:4, display:"block" }}>
          Year is optional — used to calculate age
        </span>
      </div>

      <div className="form-group">
        <label className="form-label">Notes for AI suggestions</label>
        <span className="text-sm" style={{ color:"var(--ink-hint)", margin:"0 0 6px", display:"block" }}>
          This helps HouseMind personalise gift ideas. Not shown to others.
        </span>
        <textarea className="form-textarea" value={form.interests} onChange={set("interests")}
          placeholder="Anything more specific the preset tags don't capture..." rows={2} />
      </div>

      <TagSelector
        label="Interests"
        options={INTEREST_OPTIONS}
        values={form.interest_tags}
        onChange={values => setForm(f => ({ ...f, interest_tags: values }))}
        placeholder="Add another interest"
      />

      <TagSelector
        label="Hobbies"
        options={HOBBY_OPTIONS}
        values={form.hobby_tags}
        onChange={values => setForm(f => ({ ...f, hobby_tags: values }))}
        placeholder="Add another hobby"
      />

      <div className="form-group">
        <label className="form-label">Extra avoid context</label>
        <input className="form-input" value={form.dislikes} onChange={set("dislikes")}
          placeholder="Anything the preset avoid tags don't capture..." />
      </div>

      <TagSelector
        label="Dislikes / avoid"
        options={DISLIKE_OPTIONS}
        values={form.dislike_tags}
        onChange={values => setForm(f => ({ ...f, dislike_tags: values }))}
        placeholder="Add another thing to avoid"
      />

      <div className="form-group">
        <label className="form-label">Notes</label>
        <input className="form-input" value={form.notes} onChange={set("notes")}
          placeholder="Lives in a small apartment, recently started yoga..." />
      </div>

      <div className="card card-sm mb-16" style={{ background: "var(--cream)" }}>
        <div style={{ marginBottom: 12 }}>
          <div className="text-sm" style={{ fontWeight: 600, color: "var(--ink-soft)" }}>Relationship memory</div>
          <p className="text-sm text-muted" style={{ margin: "3px 0 0" }}>
            Private details HouseMind can use for gifts, events, food, and future planning.
          </p>
        </div>
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Favorite foods</label>
            <input className="form-input" value={form.favorite_foods} onChange={set("favorite_foods")} placeholder="Sushi, jollof rice, dark chocolate..." />
          </div>
          <div className="form-group">
            <label className="form-label">Favorite stores</label>
            <input className="form-input" value={form.favorite_stores} onChange={set("favorite_stores")} placeholder="Target, Sephora, Apple, local boutiques..." />
          </div>
          <div className="form-group">
            <label className="form-label">Gift preferences</label>
            <input className="form-input" value={form.gift_preferences} onChange={set("gift_preferences")} placeholder="Prefers gift cards, experiences, practical gifts..." />
          </div>
          <div className="form-group">
            <label className="form-label">Sizes</label>
            <input className="form-input" value={form.sizes} onChange={set("sizes")} placeholder="Shirt M, shoe 8, ring size..." />
          </div>
          <div className="form-group">
            <label className="form-label">Allergies / dietary needs</label>
            <input className="form-input" value={form.allergies} onChange={set("allergies")} placeholder="Peanuts, gluten free, vegetarian..." />
          </div>
          <div className="form-group">
            <label className="form-label">Favorite restaurants</label>
            <input className="form-input" value={form.favorite_restaurants} onChange={set("favorite_restaurants")} placeholder="Favorite date night or celebration spots..." />
          </div>
          <div className="form-group">
            <label className="form-label">Traditions</label>
            <input className="form-input" value={form.traditions} onChange={set("traditions")} placeholder="Flowers on Mother's Day, brunch birthdays..." />
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Budget min ($)</label>
          <input type="number" className="form-input" value={form.budget_min} onChange={set("budget_min")} min="5" />
        </div>
        <div className="form-group">
          <label className="form-label">Budget max ($)</label>
          <input type="number" className="form-input" value={form.budget_max} onChange={set("budget_max")} min="5" />
        </div>
      </div>
      {parseInt(form.budget_min) > parseInt(form.budget_max) && (
        <div className="field-warn mb-16" style={{ marginTop: -8 }}>
          Min budget can't be higher than max — please adjust.
        </div>
      )}

      <div className="flex-center gap-8 mt-16 person-form-actions" style={{justifyContent:"flex-end"}}>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || photoSaving || parseInt(form.budget_min) > parseInt(form.budget_max)}
        >
          {loading
            ? <><span className="spinner"/> Saving...</>
            : initial.id ? "Save changes" : "Add person"}
        </button>
      </div>
    </form>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-shell" onClick={onClose}>
      <div className="card modal-card" style={{maxWidth:560}} onClick={e => e.stopPropagation()}>
        <div className="flex-between mb-16">
          <h3 style={{fontSize:20}}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PeopleSheet({ title, onClose, children, className = "", footer = null, icon = "⌂" }) {
  const { sheetRef, handleProps } = useSheetHandleDrag(onClose);

  return (
    <div
      className="fd-clear-overlay gf-edit-cover-overlay gf-people-overlay"
      onClick={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <BackButtonTrap onClose={onClose} />
      <div
        className={`gf-edit-cover-sheet gf-people-sheet ${className}`.trim()}
        ref={sheetRef}
        onClick={event => event.stopPropagation()}
      >
        <GiftSheetTopRow onClose={onClose} handleProps={handleProps} closeLabel={`Close ${title}`} />
        <GiftSheetHeader title={title} icon={icon} />
        <div className="gf-sheet-body">
          {children}
        </div>
        {footer}
      </div>
    </div>
  );
}

function FormPresentation({ title, onClose, children }) {
  return (
    <PeopleSheet title={title} onClose={onClose} className="gf-person-form-sheet">
      {children}
    </PeopleSheet>
  );
}

export default function PeoplePage() {
  const { user, applyAiUsage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteNoticeMessage, setDeleteNoticeMessage] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sharePerson, setSharePerson] = useState(null);
  const [shareTargetId, setShareTargetId] = useState("");
  const [shareMembers, setShareMembers] = useState([]);
  const [shareSaving, setShareSaving] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(null);
  const [photoSaving, setPhotoSaving] = useState(false);
  const [photoNoticeTitle, setPhotoNoticeTitle] = useState("Saving");
  const [photoNoticeMessage, setPhotoNoticeMessage] = useState("Saving photo...");
  const [showHeaderPhotoModal, setShowHeaderPhotoModal] = useState(false);
  const [headerPhotoMode, setHeaderPhotoMode] = useState(HEADER_PHOTO_MODES.AUTO);
  const [selectedHeaderPhotoIds, setSelectedHeaderPhotoIds] = useState([]);
  const [draftHeaderPhotoMode, setDraftHeaderPhotoMode] = useState(HEADER_PHOTO_MODES.AUTO);
  const [draftSelectedHeaderPhotoIds, setDraftSelectedHeaderPhotoIds] = useState([]);
  const [actionPerson, setActionPerson] = useState(null);
  const [cardMessageSheet, setCardMessageSheet] = useState(null);
  const [cardMessageLoading, setCardMessageLoading] = useState(false);
  const [pageStats, setPageStats] = useState({
    annualSpend: 0,
    upcomingCount: 0,
    pendingGifts: 0,
    pendingGiftAction: null,
    giftsGiven: 0,
    recentActivity: [],
  });
  const editCoverSheetDrag = useSheetHandleDrag(() => setShowHeaderPhotoModal(false));
  const presentation = useFormPresentation();

  const showLoading = useMinLoad(loading);
  const showSavingNotice = useMinLoad(saving, 700);
  const showDeleteNotice = useMinLoad(Boolean(deleteNoticeMessage), 700);
  const showPhotoSavingNotice = useMinLoad(photoSaving, 700);
  const showShareSavingNotice = useMinLoad(shareSaving, 700);
  const showCardMessageLoading = useMinLoad(cardMessageLoading, 700);

  useEffect(() => {
    if (deletingId) {
      setDeleteNoticeMessage("Removing person...");
    }
  }, [deletingId]);

  useOnEscape(useCallback(() => {
    if (cardMessageSheet) { setCardMessageSheet(null); return; }
    if (showHeaderPhotoModal) { setShowHeaderPhotoModal(false); return; }
    if (sharePerson) { setSharePerson(null); return; }
    if (confirmDelete) { setConfirmDelete(null); return; }
    if (showModal) { setShowModal(false); setEditing(null); setError(null); }
  }, [cardMessageSheet, showHeaderPhotoModal, sharePerson, confirmDelete, showModal]));

  const currentWorkspace = user?.workspaces?.find(workspace => workspace.id === user?.active_workspace_id) || user?.workspace || null;
  const headerPhotoStorageKey = `hm_people_header_photos_${currentWorkspace?.id || "personal"}`;
  const hasFamilyAccess = user?.plan === "family" || (user?.workspaces || []).some(workspace => workspace.plan === "family");
  const canShareFromHere = hasFamilyAccess && (currentWorkspace?.workspace_type || "personal") === "personal";

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(headerPhotoStorageKey) || "{}");
      setHeaderPhotoMode(Object.values(HEADER_PHOTO_MODES).includes(saved.mode) ? saved.mode : HEADER_PHOTO_MODES.AUTO);
      setSelectedHeaderPhotoIds(Array.isArray(saved.selectedIds) ? saved.selectedIds.map(String).slice(0, HEADER_PHOTO_LIMIT) : []);
    } catch {
      setHeaderPhotoMode(HEADER_PHOTO_MODES.AUTO);
      setSelectedHeaderPhotoIds([]);
    }
  }, [headerPhotoStorageKey]);

  const saveHeaderPhotoPrefs = useCallback((mode, selectedIds = selectedHeaderPhotoIds) => {
    const nextIds = selectedIds.map(String).slice(0, HEADER_PHOTO_LIMIT);
    setHeaderPhotoMode(mode);
    setSelectedHeaderPhotoIds(nextIds);
    try {
      localStorage.setItem(headerPhotoStorageKey, JSON.stringify({ mode, selectedIds: nextIds }));
    } catch {
      // If browser storage is unavailable, keep the preference for this session.
    }
  }, [headerPhotoStorageKey, selectedHeaderPhotoIds]);

  const openEditCover = () => {
    setDraftHeaderPhotoMode(headerPhotoMode);
    setDraftSelectedHeaderPhotoIds(selectedHeaderPhotoIds);
    setShowHeaderPhotoModal(true);
  };

  const saveEditCover = () => {
    saveHeaderPhotoPrefs(draftHeaderPhotoMode, draftSelectedHeaderPhotoIds);
    setShowHeaderPhotoModal(false);
  };

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    peopleApi.listDetailed()
      .then(data => {
        if (ignore) return;
        if (Array.isArray(data)) {
          setPeople(data);
          setPageStats({ annualSpend: 0, upcomingCount: 0, pendingGifts: 0, pendingGiftAction: null, giftsGiven: 0, recentActivity: [] });
          return;
        }
        setPeople(data.people || []);
        setPageStats({
          annualSpend: Number(data.annual_spend || 0),
          upcomingCount: Number(data.upcoming_count || 0),
          pendingGifts: Number(data.pending_gifts || 0),
          pendingGiftAction: data.pending_gift_action || null,
          giftsGiven: Number(data.gifts_given || 0),
          recentActivity: Array.isArray(data.recent_activity) ? data.recent_activity : [],
        });
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [user?.active_workspace_id]);

  useEffect(() => {
    if (!location.state?.edit || people.length === 0) return;
    const match = people.find(person => person.id === location.state.edit);
    if (match) {
      setEditing(match);
      setError(null);
      setShowModal(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate, people]);

  const handleSave = async data => {
    setSaving(true);
    setError(null);
    try {
      if (editing?.id) {
        const updated = await peopleApi.update(editing.id, data);
        setPeople(p=>p.map(x=>x.id===updated.id ? updated : x));
        toast?.(`${updated.name} updated`, "success");
      } else {
        const created = await peopleApi.create(data);
        setPeople(p=>[...p, created]);
        toast?.(`${created.name} added`, "success");
        if (data?.birth_date && created?.id) {
          navigate(`/app/people/${created.id}`);
        }
      }
      setShowModal(false);
      setEditing(null);
    } catch(e) {
      const msg = e.data?.upgrade_required
        ? "Free plan is limited to 5 people. Upgrade to Pro for unlimited."
        : e.message;
      setError(msg);
      toast?.(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async person => {
    setDeletingId(person.id);
    try {
      await peopleApi.delete(person.id);
      setPeople(p=>p.filter(x=>x.id!==person.id));
      toast?.(`${person.name} removed`, "info");
    } finally {
      setDeletingId(null);
      setDeleteNoticeMessage("");
    }
  };

  const handlePhotoAutoSave = async (data, { isRemoving = false } = {}) => {
    if (!editing?.id) return;
    setPhotoNoticeTitle(isRemoving ? "Removing" : "Saving");
    setPhotoNoticeMessage(isRemoving ? "Removing profile photo..." : "Saving profile photo...");
    setPhotoSaving(true);
    setError(null);
    try {
      const updated = await peopleApi.update(editing.id, data);
      setPeople((current) => current.map((person) => (person.id === updated.id ? updated : person)));
      setEditing(updated);
      toast?.(isRemoving ? "Profile photo removed" : "Profile photo updated", "success");
    } catch (e) {
      const msg = e.data?.upgrade_required
        ? "Free plan is limited to 5 people. Upgrade to Pro for unlimited."
        : e.message;
      setError(msg);
      toast?.(msg, "error");
      throw e;
    } finally {
      setPhotoSaving(false);
    }
  };

  const openShare = async person => {
    setSharePerson(person);
    setShareTargetId("");
    setShareMembers([]);
    setShareError(null);
    try {
      const data = await sharingApi.members();
      const members = Array.isArray(data.members) ? data.members : [];
      setShareMembers(members);
      setShareTargetId(members[0]?.user_id ? String(members[0].user_id) : "");
      if (members.length === 0) {
        setShareError("Add another Family member in Settings before sharing.");
      }
    } catch (e) {
      setShareError(e.message);
    }
  };

  const handleShare = async () => {
    if (!sharePerson || !shareTargetId) return;
    setShareSaving(true);
    setShareError(null);
    try {
      const targetMember = shareMembers.find(member => String(member.user_id) === String(shareTargetId));
      await sharingApi.create({
        resource_type: "person",
        resource_id: sharePerson.id,
        recipient_user_id: Number(shareTargetId),
      });
      setSharePerson(null);
      const successMsg = `${sharePerson.name} shared`;
      toast?.(successMsg, "success");
      setShareSuccess({
        title: "Shared",
        message: `${sharePerson.name} is now visible in Shared with me for ${targetMember?.full_name || "that Family member"}. Your private account stays separate, and nothing else was shared.`,
      });
    } catch (e) {
      setShareError(e.message);
    } finally {
      setShareSaving(false);
    }
  };

  const openAdd = () => { setEditing(null); setError(null); setShowModal(true); };
  const openEdit = p => { setEditing(p); setError(null); setShowModal(true); };
  const openGiftAsk = useCallback((question = "") => {
    const clean = String(question || "").trim();
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: clean || "Ask about People & Gifts.",
        context: {
          module: "people",
          module_label: "People & Gifts",
          surface: "People & Gifts",
        },
      },
    }));
  }, []);

  const closeActionSheet = useCallback(() => setActionPerson(null), []);

  if (showLoading) return (
    <div className="loading-spinner-center">
      <span className="spinner" />
      Loading your people…
    </div>
  );

  const uploadedPhotoPeople = people
    .filter(person => isUploadedProfilePhoto(person.photo_url));
  const selectedUploadedPhotoPeople = uploadedPhotoPeople
    .filter(person => selectedHeaderPhotoIds.includes(String(person.id)));
  const headerPhotoPeople = headerPhotoMode === HEADER_PHOTO_MODES.DEFAULT
    ? []
    : headerPhotoMode === HEADER_PHOTO_MODES.CHOOSE
      ? selectedUploadedPhotoPeople.slice(0, HEADER_PHOTO_LIMIT)
      : uploadedPhotoPeople.slice(0, HEADER_PHOTO_LIMIT);
  const peopleHeaderImages = headerPhotoPeople.map(person => person.photo_url);
  const upcomingOccasions = people
    .flatMap(person => sortUpcomingOccasions(person).slice(0, 1).map(occasion => ({ person, occasion })))
    .sort((a, b) => (a.occasion.days_until ?? 9999) - (b.occasion.days_until ?? 9999));
  const upcomingOccasionChips = upcomingOccasions.slice(0, 8);
  const nextOccasion = upcomingOccasions[0] || null;
  const nextOccasionDrawerItem = nextOccasion && (nextOccasion.occasion.days_until ?? 9999) <= 365 ? nextOccasion : null;
  const annualSpend = Number(pageStats.annualSpend || 0);
  const pendingGiftCount = Number(pageStats.pendingGifts || 0);
  const pendingGiftAction = pageStats.pendingGiftAction;
  const openGiftAction = (action) => {
    if (!action?.person_id || !action?.occasion_id) return;
    navigate(`/app/people/${action.person_id}/gifts/${action.occasion_id}`);
  };
  const openGiftIdeas = (person, occasion) => {
    if (!person?.id || !occasion?.id) return;
    navigate(`/app/people/${person.id}/gifts/${occasion.id}`);
  };
  const writeCardMessage = async (person, occasion) => {
    if (!person?.id || !occasion?.id || cardMessageLoading) return;
    setCardMessageLoading(true);
    try {
      const data = await giftsApi.getCardMessage(occasion.id, { provider: user?.ai_provider || "openai", tone: "warm" });
      applyAiUsage?.(data.ai_usage);
      setCardMessageSheet({
        personName: person.name,
        occasionType: occasion.occasion_type,
        message: data.message || "",
      });
      toast?.("Card message ready", "success");
    } catch (e) {
      if (e.data?.ai_usage) applyAiUsage?.(e.data.ai_usage);
      const message = e.data?.upgrade_required
        ? "You've used your free AI suggestions. Upgrade to keep getting personalized ideas and planning tools."
        : e.message;
      toast?.(message || "Could not write card message.", "error");
    } finally {
      setCardMessageLoading(false);
    }
  };

  return (
    <div className="hm-gifts-page">
      <ModuleHeader
        title="People & Gifts"
        subtitle={people.length === 0
          ? "People, occasions, gift ideas, and history"
          : `${people.length} people · occasions, gift ideas & history`}
        image={peopleHeaderImages[0] || IMG_PEOPLE}
        galleryImages={peopleHeaderImages}
      >
        {people.length > 0 ? (
          <>
            <div className="hm-module-stat">{people.length}<span>people</span></div>
            <div className="hm-module-stat">{upcomingOccasionChips.length}<span>upcoming</span></div>
            {pendingGiftCount > 0 ? (
              <div className="hm-module-stat hm-module-stat--alert">{pendingGiftCount}<span>gifts</span></div>
            ) : null}
          </>
        ) : null}
      </ModuleHeader>
      <div className="hm-gifts-list-toolbar" style={{ display:"flex", justifyContent:"flex-end", padding:"16px 0 4px" }}>
        <span>All people</span>
        <div className="flex-center gap-8">
          {uploadedPhotoPeople.length > 0 && (
            <button className="btn btn-secondary gf-edit-cover-btn" onClick={openEditCover}>
              <span aria-hidden="true">▧</span>
              Edit cover
            </button>
          )}
          <button className="hm-btn hm-btn--primary" onClick={openAdd}>+ Add person</button>
        </div>
      </div>
      <GiftsIntelligenceHeader
        nextOccasion={nextOccasion}
        pendingGiftCount={pendingGiftCount}
        pendingGiftAction={pendingGiftAction}
        annualSpend={annualSpend}
        peopleCount={people.length}
        onOpenGiftAction={openGiftAction}
      />
      <NextOccasionDrawer
        item={nextOccasionDrawerItem}
        onGiftIdeas={openGiftIdeas}
        onCardMessage={writeCardMessage}
        cardLoading={cardMessageLoading}
      />
      {upcomingOccasionChips.length > 0 && (
        <div className="hm-gifts-occasion-strip" aria-label="Upcoming occasions">
          {upcomingOccasionChips.map(({ person, occasion }) => {
            const [chipBg, chipFg] = getAvatarColor(person.name);
            return (
              <button
                key={`${person.id}-${occasion.id}`}
                type="button"
                className={`hm-gifts-occasion-chip${(occasion.days_until ?? 9999) <= 14 ? " is-urgent" : ""}`}
                onClick={() => navigate(`/app/people/${person.id}?occasion_id=${occasion.id}`)}
              >
                <span className="hm-gifts-occasion-avatar" style={{ background: chipBg, color: chipFg }}>{getInitials(person.name)}</span>
                <b className="hm-gifts-occasion-icon" aria-hidden="true">{occasionEmoji(occasion.occasion_type)}</b>
                <strong>{person.name}'s {occasionLabel(occasion.occasion_type)}</strong>
                <em>{dayLabel(occasion.days_until)}</em>
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <div className="notice-banner is-error mb-16">
          <p>{error}</p>
        </div>
      )}

      {people.length===0 ? (
        <div className="card empty-state-panel">
          <div className="empty-state-icon">◉</div>
          <h3 className="empty-state-title">Add your first person</h3>
          <p className="text-muted mb-16">Start with someone whose birthday is coming up.</p>
          <button className="btn btn-primary btn-lg" onClick={openAdd}>Add a person</button>
        </div>
      ) : (
        <div className="page-list-stack">
          {people.map(person => {
            const [bg, fg] = getAvatarColor(person.name);
            const initials = person.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
            const nextOcc = sortUpcomingOccasions(person)[0];
            const giftCount = Number(person.gift_count || 0);
            const personAnnualSpend = Number(person.annual_spend || 0);
            const hasGiftStats = giftCount > 0 || personAnnualSpend > 0;
            return (
              <div key={person.id} className={`card card-sm person-row-card ${urgencyClass(nextOcc?.days_until)}`}
                style={{ gap:16, cursor:"pointer" }}
                onClick={() => {
                  if (presentation === "modal") {
                    navigate(`/app/people/${person.id}`);
                    return;
                  }
                  setActionPerson(person);
                }}>

                <div className="person-row-avatar" style={{
                  width:44, height:44, borderRadius:"50%", flexShrink:0,
                  background: person.photo_url ? "transparent" : bg,
                  color:fg, display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:18, fontFamily:"var(--font-display)", overflow:"hidden",
                }}>
                  {person.photo_url
                    ? <img src={person.photo_url} alt={person.name} style={{width:"100%",height:"100%",objectFit:"cover"}} />
                    : initials}
                </div>

                <div className="person-row-summary">
                  <div className="content-title-row">
                    <span className="person-row-name" style={{fontWeight:500}}>{person.name}</span>
                    <span className="content-meta-chip">{person.relationship}</span>
                    {person.age ? <span className="content-meta-chip">{person.age}y</span> : null}
                  </div>
                  {nextOcc && (
                    <div className="person-row-next">
                      <div className="text-sm person-row-next-label" style={{textTransform:"capitalize"}}>
                        {occasionLabel(nextOcc.occasion_type)}
                      </div>
                      <span className={`days-pill ${nextOcc.days_until<=14?"days-urgent":nextOcc.days_until<=30?"days-soon":"days-ok"}`}>
                        {nextOcc.days_until===0?"Today!":nextOcc.days_until===1?"Tomorrow":`${nextOcc.days_until}d`}
                      </span>
                    </div>
                  )}
                  {hasGiftStats && (
                    <div className="gf-person-gift-stat">
                      {giftCount} gift{giftCount === 1 ? "" : "s"}
                    </div>
                  )}
                </div>

                {hasGiftStats && (
                  <div className="gf-person-card-spend">
                    <strong>${personAnnualSpend.toFixed(0)}</strong>
                  </div>
                )}

                {!hasGiftStats && (
                  <div className="gf-person-card-spend gf-person-card-spend--empty" aria-hidden="true" />
                )}

                <div className="hm-gifts-row-right" aria-hidden="true">
                  <span className="hm-gifts-row-chevron">›</span>
                </div>

                <div className="flex-center gap-8 person-row-actions person-row-actions-desktop" onClick={e=>e.stopPropagation()}>
                  <button className="btn btn-secondary btn-sm" onClick={()=>openEdit(person)}>Edit</button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/app/people/${person.id}?add_occasion=1`);
                    }}
                  >
                    + Occasion
                  </button>
                  {canShareFromHere && (
                    <button className="btn btn-secondary btn-sm" onClick={() => openShare(person)}>
                      Share with Family
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" style={{color:"var(--rose)"}}
                    onClick={()=>setConfirmDelete(person)} disabled={deletingId===person.id}>
                    {deletingId===person.id ? <span className="spinner" style={{ borderColor:"rgba(231,97,74,0.2)", borderTopColor:"var(--rose)", width:12, height:12 }} /> : "Remove"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="fd-food-askbar gf-askbar">
        <input
          className="fd-food-askbar__input"
          type="text"
          placeholder="✦ Ask AI for gifts, budgets, or birthday ideas..."
          aria-label="Ask HouseMind AI about gifts"
          onFocus={() => openGiftAsk("")}
          readOnly
        />
        <div className="fd-food-askbar__chips">
          {["Gift ideas", "Budget help", "Birthday plan"].map(prompt => (
            <button
              key={prompt}
              type="button"
              className="fd-food-askbar__chip"
              onClick={() => openGiftAsk(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="fd-food-askbar__button"
          onClick={() => openGiftAsk("Gift ideas")}
        >
          ✦ Ask
        </button>
      </div>

      <PersonActionSheet
        person={actionPerson}
        canShare={canShareFromHere}
        onClose={closeActionSheet}
        onOpenProfile={() => {
          if (!actionPerson) return;
          const personId = actionPerson.id;
          setActionPerson(null);
          navigate(`/app/people/${personId}`);
        }}
        onViewOccasions={() => {
          if (!actionPerson) return;
          const personId = actionPerson.id;
          setActionPerson(null);
          navigate(`/app/people/${personId}?section=occasions`);
        }}
        onAddOccasion={() => {
          if (!actionPerson) return;
          const personId = actionPerson.id;
          setActionPerson(null);
          navigate(`/app/people/${personId}?add_occasion=1`);
        }}
        onEdit={() => {
          if (!actionPerson) return;
          openEdit(actionPerson);
          setActionPerson(null);
        }}
        onShare={() => {
          if (!actionPerson) return;
          openShare(actionPerson);
          setActionPerson(null);
        }}
        onRemove={() => {
          setConfirmDelete(actionPerson);
          closeActionSheet();
        }}
      />

      {/* Add / Edit person — 3-step bottom sheet on mobile */}
      {showModal && presentation === "sheet" && (
        <AddPersonSheet
          key={editing?.id || "new"}
          initial={editing || {}}
          title={editing ? `Edit ${editing.name}` : "Add a person"}
          submitLabel={editing ? "Save changes" : "✓ Save person"}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditing(null); setError(null); }}
          loading={saving}
          error={error}
        />
      )}

      {/* Add / Edit person — full bottom sheet on wider screens */}
      {showModal && presentation === "modal" && (
        <FormPresentation
          title={editing ? `Edit ${editing.name}` : "Add a person"}
          onClose={() => { setShowModal(false); setEditing(null); setError(null); }}>
          {error && (
            <div className="notice-banner is-error mb-16"><p>{error}</p></div>
          )}
          <PersonForm
            initial={editing || {}}
            onSave={handleSave}
            onPhotoAutoSave={handlePhotoAutoSave}
            onCancel={() => { setShowModal(false); setEditing(null); setError(null); }}
            loading={saving}
            photoSaving={photoSaving}
          />
        </FormPresentation>
      )}

      {confirmDelete && (
        <ConfirmModal
          tag="⚠ Remove person"
          title={`Remove ${confirmDelete.name}?`}
          message="All occasions and gift history for this person will be permanently deleted."
          confirmLabel="Remove person"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={async () => {
            const person = confirmDelete;
            setConfirmDelete(null);
            await handleDelete(person);
          }}
          loading={deletingId === confirmDelete.id}
        />
      )}

      {sharePerson && (
        <PeopleSheet title="Share with Family" icon="👥" onClose={() => { setSharePerson(null); setShareError(null); }} className="gf-share-sheet">
          <div className="notice-banner mb-16">
            <p>
              This shares a view of {sharePerson.name} with one Family member. Your private account stays separate,
              and the rest of your people, gifts, food, trips, records, vehicles, subscriptions, and utilities remain private.
            </p>
          </div>
          {shareError && (
            <div className="notice-banner is-error mb-16">
              <p>{shareError}</p>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Family member</label>
            <select className="form-select" value={shareTargetId} onChange={e => setShareTargetId(e.target.value)}>
              {shareMembers.map(member => (
                <option key={member.user_id} value={member.user_id}>{member.full_name} - {member.email}</option>
              ))}
            </select>
          </div>
          <div className="flex-between" style={{ justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={() => { setSharePerson(null); setShareError(null); }} disabled={shareSaving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleShare} disabled={shareSaving || !shareTargetId}>
              {shareSaving ? "Sharing..." : "Share with Family"}
            </button>
          </div>
        </PeopleSheet>
      )}

      {shareSuccess && (
        <Modal title={shareSuccess.title} onClose={() => setShareSuccess(null)}>
          <div className="notice-banner mb-16">
            <p>{shareSuccess.message}</p>
          </div>
          <div className="flex-between" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-primary" onClick={() => setShareSuccess(null)}>
              OK
            </button>
          </div>
        </Modal>
      )}

      {showHeaderPhotoModal && (
        <div
          className="fd-clear-overlay gf-edit-cover-overlay"
          onClick={event => {
            if (event.target === event.currentTarget) setShowHeaderPhotoModal(false);
          }}
        >
          <div
            className="gf-edit-cover-sheet"
            ref={editCoverSheetDrag.sheetRef}
            onClick={event => event.stopPropagation()}
          >
            <GiftSheetTopRow onClose={() => setShowHeaderPhotoModal(false)} handleProps={editCoverSheetDrag.handleProps} closeLabel="Close edit cover" />
            <GiftSheetHeader title="Edit cover" />

            <div className="gf-edit-cover-body">
              <p className="gf-edit-cover-desc">
                Choose what appears in the People & Gifts header. Up to {HEADER_PHOTO_LIMIT} uploaded profile photos can be shown.
              </p>
              <div className="gf-cover-mode-row">
                {[
                  [HEADER_PHOTO_MODES.AUTO, "Auto"],
                  [HEADER_PHOTO_MODES.CHOOSE, "Choose photos"],
                  [HEADER_PHOTO_MODES.DEFAULT, "Default image"],
                ].map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    className={`gf-cover-mode${draftHeaderPhotoMode === mode ? " gf-cover-mode--on" : ""}`}
                    onClick={() => setDraftHeaderPhotoMode(mode)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {uploadedPhotoPeople.length > 0 ? (
                <>
                  <div className="gf-cover-grid">
                    {uploadedPhotoPeople.map(person => {
                      const personId = String(person.id);
                      const chooseMode = draftHeaderPhotoMode === HEADER_PHOTO_MODES.CHOOSE;
                      const checked = chooseMode && draftSelectedHeaderPhotoIds.includes(personId);
                      const atLimit = chooseMode && draftSelectedHeaderPhotoIds.length >= HEADER_PHOTO_LIMIT;
                      return (
                        <button
                          key={person.id}
                          type="button"
                          className={`gf-cover-photo${checked ? " gf-cover-photo--on" : ""}`}
                          disabled={atLimit && !checked}
                          onClick={() => {
                            if (!chooseMode) {
                              setDraftHeaderPhotoMode(HEADER_PHOTO_MODES.CHOOSE);
                              setDraftSelectedHeaderPhotoIds([personId]);
                              return;
                            }
                            const nextIds = checked
                              ? draftSelectedHeaderPhotoIds.filter(id => id !== personId)
                              : [...draftSelectedHeaderPhotoIds, personId].slice(0, HEADER_PHOTO_LIMIT);
                            setDraftSelectedHeaderPhotoIds(nextIds);
                          }}
                        >
                          <img src={person.photo_url} alt="" />
                          <span className="gf-cover-check" aria-hidden="true">✓</span>
                          <span className="gf-cover-name">{person.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="gf-cover-count">
                    {draftHeaderPhotoMode === HEADER_PHOTO_MODES.CHOOSE
                      ? `${draftSelectedHeaderPhotoIds.length} of ${HEADER_PHOTO_LIMIT} selected`
                      : draftHeaderPhotoMode === HEADER_PHOTO_MODES.AUTO
                        ? `Showing up to ${Math.min(uploadedPhotoPeople.length, HEADER_PHOTO_LIMIT)} photos automatically`
                        : "Default image selected. Photos stay available if you switch back."}
                  </p>
                </>
              ) : (
                <div className="gf-cover-empty">
                  Upload profile photos on people cards to use them in the cover.
                </div>
              )}
            </div>

            <div className="fd-clear-sheet__foot gf-sheet-foot">
              <button type="button" className="fd-clear-cancel" onClick={() => setShowHeaderPhotoModal(false)}>
                Cancel
              </button>
              <button type="button" className="hm-btn hm-btn--primary fd-clear-confirm" onClick={saveEditCover}>
                Done ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {cardMessageSheet && (
        <PeopleSheet
          title="Card message"
          icon="💌"
          onClose={() => setCardMessageSheet(null)}
          className="gift-card-message-sheet"
        >
          <div className="gf-card-result">
            <div className="gf-card-result__label">
              ✦ {cardMessageSheet.personName}'s {occasionLabel(cardMessageSheet.occasionType)}
            </div>
            <div className="gf-card-result__text">{cardMessageSheet.message}</div>
            <div className="flex-center gap-8" style={{ justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  navigator.clipboard?.writeText(cardMessageSheet.message);
                  toast?.("Copied", "success");
                }}
              >
                Copy
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setCardMessageSheet(null)}>
                Done
              </button>
            </div>
          </div>
        </PeopleSheet>
      )}

      {showDeleteNotice && <SavingNotice title="Removing" message={deleteNoticeMessage || "Removing person..."} />}
      {showSavingNotice && <SavingNotice message="Saving person..." />}
      {showPhotoSavingNotice && <SavingNotice title={photoNoticeTitle} message={photoNoticeMessage} />}
      {showShareSavingNotice && <SavingNotice title="Sharing" message="Sharing with Family..." />}
      {showCardMessageLoading && <SavingNotice title="Writing" message="Writing card message..." />}
    </div>
  );
}
